import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface Workstation {
  id: string;
  code: string;
  name: string;
  type: string;
  portal?: string;
}

interface InstructionSubStep {
  step_number: number;
  text: string;
  photo_url?: string;
  safety_note?: string;
}

interface MaterialNeeded {
  part_number?: string;
  inventory_item_id?: string;
  description: string;
  qty: number;
  photo_url?: string;
}

interface WorkstationStep {
  id: string;
  workstation_id: string;
  step_number: number;
  step_name: string;
  instruction_steps: InstructionSubStep[];
  materials_needed: MaterialNeeded[];
  safety_note?: string;
  tool_requirements?: string;
  quality_checks: string[];
  estimated_minutes?: number;
  is_active: boolean;
}

// Heavy logging helper
const log = (action: string, data?: any) => {
  console.log(`[WORKSTATION_STEPS_PAGE] ${action}`, data !== undefined ? data : '');
};

const logError = (action: string, error: any) => {
  console.error(`[WORKSTATION_STEPS_PAGE] ERROR - ${action}:`, error);
  console.error(`[WORKSTATION_STEPS_PAGE] Error type:`, typeof error);
  console.error(`[WORKSTATION_STEPS_PAGE] Error message:`, error?.message);
  console.error(`[WORKSTATION_STEPS_PAGE] Error stack:`, error?.stack);
};

export default function WorkstationStepsPage() {
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>('');
  const [steps, setSteps] = useState<WorkstationStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingStep, setEditingStep] = useState<WorkstationStep | null>(null);
  const [isNewStep, setIsNewStep] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  log('Component mounted');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_auth_token');
    log('getAuthHeaders - token exists:', !!token);
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  useEffect(() => {
    log('Initial useEffect - loading workstations');
    loadWorkstations();
  }, []);

  useEffect(() => {
    log('selectedWorkstation useEffect triggered:', selectedWorkstation);
    if (selectedWorkstation) {
      loadSteps(selectedWorkstation);
    } else {
      setSteps([]);
    }
  }, [selectedWorkstation]);

  const loadWorkstations = async () => {
    log('loadWorkstations - START');
    setError(null);
    try {
      const url = `${API_BASE}/production/workstations`;
      log('loadWorkstations - Fetching:', url);

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      log('loadWorkstations - Response status:', response.status);
      log('loadWorkstations - Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        log('loadWorkstations - Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      log('loadWorkstations - RAW RESPONSE:', data);
      log('loadWorkstations - Response type:', typeof data);
      log('loadWorkstations - Is array:', Array.isArray(data));

      if (!Array.isArray(data)) {
        throw new Error(`Expected array, got ${typeof data}: ${JSON.stringify(data)}`);
      }

      setWorkstations(data);
      log('loadWorkstations - SUCCESS, count:', data.length);
    } catch (error: any) {
      logError('loadWorkstations', error);
      setError(`Failed to load workstations: ${error?.message || 'Unknown error'}`);
      showToast('Failed to load workstations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSteps = async (workstationId: string) => {
    log('loadSteps - START, workstationId:', workstationId);
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE}/production/workstation-steps/${workstationId}`;
      log('loadSteps - Fetching:', url);

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      log('loadSteps - Response status:', response.status);
      log('loadSteps - Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        log('loadSteps - Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      log('loadSteps - RAW RESPONSE:', data);
      log('loadSteps - Response type:', typeof data);
      log('loadSteps - Is array:', Array.isArray(data));

      if (!Array.isArray(data)) {
        throw new Error(`Expected array, got ${typeof data}: ${JSON.stringify(data)}`);
      }

      setSteps(data);
      log('loadSteps - SUCCESS, count:', data.length);
    } catch (error: any) {
      logError('loadSteps', error);
      setError(`Failed to load steps: ${error?.message || 'Unknown error'}`);
      showToast('Failed to load steps', 'error');
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveStep = async (stepIndex: number, direction: 'up' | 'down') => {
    log('handleMoveStep - START', { stepIndex, direction });
    if (direction === 'up' && stepIndex === 0) return;
    if (direction === 'down' && stepIndex === steps.length - 1) return;

    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    [newSteps[stepIndex], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[stepIndex]];
    setSteps(newSteps);

    try {
      const response = await fetch(`${API_BASE}/production/workstation-steps/reorder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          workstation_id: selectedWorkstation,
          step_ids: newSteps.map((s) => s.id),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      showToast('Steps reordered', 'success');
      loadSteps(selectedWorkstation);
      log('handleMoveStep - SUCCESS');
    } catch (error) {
      logError('handleMoveStep', error);
      showToast('Failed to save order', 'error');
      loadSteps(selectedWorkstation);
    }
  };

  const handleAddStep = () => {
    log('handleAddStep - START');
    setIsNewStep(true);
    setEditingStep({
      id: '',
      workstation_id: selectedWorkstation,
      step_number: steps.length + 1,
      step_name: '',
      instruction_steps: [{ step_number: 1, text: '' }],
      materials_needed: [],
      quality_checks: [],
      is_active: true,
    });
  };

  const handleSaveStep = async () => {
    log('handleSaveStep - START');
    if (!editingStep) return;
    setSaving(true);

    try {
      const endpoint = isNewStep
        ? `${API_BASE}/production/workstation-steps`
        : `${API_BASE}/production/workstation-steps/${editingStep.id}`;

      log('handleSaveStep - Endpoint:', endpoint);
      log('handleSaveStep - Payload:', editingStep);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          workstation_id: editingStep.workstation_id,
          step_number: editingStep.step_number,
          step_name: editingStep.step_name,
          instruction_steps: editingStep.instruction_steps,
          materials_needed: editingStep.materials_needed,
          safety_note: editingStep.safety_note,
          tool_requirements: editingStep.tool_requirements,
          quality_checks: editingStep.quality_checks,
          estimated_minutes: editingStep.estimated_minutes,
        }),
      });

      log('handleSaveStep - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        log('handleSaveStep - Error response:', errorText);
        throw new Error(`Failed to save: ${errorText}`);
      }

      showToast(isNewStep ? 'Step created' : 'Step updated', 'success');
      setEditingStep(null);
      setIsNewStep(false);
      loadSteps(selectedWorkstation);
      log('handleSaveStep - SUCCESS');
    } catch (error) {
      logError('handleSaveStep', error);
      showToast('Failed to save step', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    log('handleDeleteStep - START, stepId:', stepId);
    if (!confirm('Delete this step?')) return;

    try {
      const response = await fetch(`${API_BASE}/production/workstation-steps/${stepId}/delete`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      showToast('Step deleted', 'success');
      loadSteps(selectedWorkstation);
      log('handleDeleteStep - SUCCESS');
    } catch (error) {
      logError('handleDeleteStep', error);
      showToast('Failed to delete step', 'error');
    }
  };

  const toggleExpand = (stepId: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const addInstructionSubStep = () => {
    if (!editingStep) return;
    setEditingStep({
      ...editingStep,
      instruction_steps: [
        ...editingStep.instruction_steps,
        { step_number: editingStep.instruction_steps.length + 1, text: '' },
      ],
    });
  };

  const removeInstructionSubStep = (index: number) => {
    if (!editingStep) return;
    const newSteps = editingStep.instruction_steps.filter((_, i) => i !== index);
    setEditingStep({
      ...editingStep,
      instruction_steps: newSteps.map((s, i) => ({ ...s, step_number: i + 1 })),
    });
  };

  const updateInstructionSubStep = (index: number, field: string, value: string) => {
    if (!editingStep) return;
    const newSteps = [...editingStep.instruction_steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setEditingStep({ ...editingStep, instruction_steps: newSteps });
  };

  const addQualityCheck = () => {
    if (!editingStep) return;
    setEditingStep({
      ...editingStep,
      quality_checks: [...editingStep.quality_checks, ''],
    });
  };

  const removeQualityCheck = (index: number) => {
    if (!editingStep) return;
    setEditingStep({
      ...editingStep,
      quality_checks: editingStep.quality_checks.filter((_, i) => i !== index),
    });
  };

  const updateQualityCheck = (index: number, value: string) => {
    if (!editingStep) return;
    const newChecks = [...editingStep.quality_checks];
    newChecks[index] = value;
    setEditingStep({ ...editingStep, quality_checks: newChecks });
  };

  return (
    <div className="p-6">
      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <strong>Error:</strong> {error}
          <button
            onClick={() => { setError(null); loadWorkstations(); }}
            className="ml-4 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workstation Step Editor</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Configure step-by-step instructions for each workstation
        </p>
      </div>

      {/* Workstation Selector */}
      <div className="mb-6 flex gap-4 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Select Workstation
          </label>
          <select
            value={selectedWorkstation}
            onChange={(e) => setSelectedWorkstation(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">Choose a workstation...</option>
            <optgroup label="Factory Stations">
              {workstations
                .filter((ws) => (ws.portal || 'factory') === 'factory')
                .map((ws) => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} ({ws.code})
                  </option>
                ))}
            </optgroup>
            <optgroup label="Shipping Stations">
              {workstations
                .filter((ws) => (ws.portal || 'factory') === 'shipping')
                .map((ws) => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} ({ws.code})
                  </option>
                ))}
            </optgroup>
          </select>
        </div>

        {selectedWorkstation && (
          <button
            onClick={handleAddStep}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Step
          </button>
        )}
      </div>

      {/* Steps List */}
      {selectedWorkstation && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading steps...</div>
          ) : steps.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No steps configured for this workstation. Click "Add Step" to create one.
            </div>
          ) : (
            <div className="space-y-3">
              {steps.map((step, index) => {
                const isExpanded = expandedSteps.has(step.id);
                return (
                <div
                  key={step.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Move buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveStep(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveStep(index, 'down')}
                        disabled={index === steps.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Step number */}
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full font-bold flex-shrink-0">
                      {step.step_number}
                    </div>

                    {/* Step info - clickable to expand */}
                    <button
                      onClick={() => toggleExpand(step.id)}
                      className="flex-1 text-left flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{step.step_name}</h3>
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex gap-4">
                          <span>{step.instruction_steps?.length || 0} instruction steps</span>
                          <span>{step.quality_checks?.length || 0} quality checks</span>
                          {step.estimated_minutes && <span>~{step.estimated_minutes} min</span>}
                        </div>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Actions */}
                    <button
                      onClick={() => {
                        setIsNewStep(false);
                        setEditingStep(step);
                      }}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg flex-shrink-0"
                      title="Edit step"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteStep(step.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg flex-shrink-0"
                      title="Delete step"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 space-y-4">
                      {/* Instruction Steps */}
                      {step.instruction_steps?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">Instructions</h4>
                          <ol className="space-y-1.5">
                            {step.instruction_steps.map((sub, i) => (
                              <li key={i} className="flex gap-2 text-sm">
                                <span className="w-5 h-5 flex items-center justify-center bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                                  {sub.step_number}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <span className="text-gray-800 dark:text-gray-200">{sub.text}</span>
                                  {sub.safety_note && (
                                    <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">⚠ {sub.safety_note}</span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Quality Checks */}
                      {step.quality_checks?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2">Quality Checks</h4>
                          <ul className="space-y-1">
                            {step.quality_checks.map((check, i) => (
                              <li key={i} className="flex gap-2 text-sm text-gray-800 dark:text-gray-200">
                                <span className="text-green-500 flex-shrink-0">✓</span>
                                {check}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Tool Requirements & Safety Note */}
                      <div className="flex gap-6 flex-wrap">
                        {step.tool_requirements && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Tools</h4>
                            <p className="text-sm text-gray-800 dark:text-gray-200">{step.tool_requirements}</p>
                          </div>
                        )}
                        {step.safety_note && (
                          <div>
                            <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">Safety Note</h4>
                            <p className="text-sm text-amber-700 dark:text-amber-300">{step.safety_note}</p>
                          </div>
                        )}
                      </div>

                      {/* Empty state */}
                      {(!step.instruction_steps || step.instruction_steps.length === 0) &&
                       (!step.quality_checks || step.quality_checks.length === 0) &&
                       !step.tool_requirements && !step.safety_note && (
                        <p className="text-sm text-gray-400 italic">No details configured yet. Click the edit button to add content.</p>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Step Modal */}
      {editingStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full my-8">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {isNewStep ? 'Add New Step' : 'Edit Step'}
              </h2>
              <button
                onClick={() => {
                  setEditingStep(null);
                  setIsNewStep(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Step Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Step Name *
                </label>
                <input
                  type="text"
                  value={editingStep.step_name}
                  onChange={(e) => setEditingStep({ ...editingStep, step_name: e.target.value })}
                  placeholder="e.g., Cut Roller Tube"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Instruction Sub-Steps */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Instruction Steps (with photos)
                  </label>
                  <button
                    onClick={addInstructionSubStep}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add Step
                  </button>
                </div>
                <div className="space-y-3">
                  {editingStep.instruction_steps.map((subStep, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                      <div className="flex gap-3 items-start">
                        <span className="w-6 h-6 flex items-center justify-center bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full text-sm font-bold flex-shrink-0 mt-2">
                          {subStep.step_number}
                        </span>
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={subStep.text}
                            onChange={(e) => updateInstructionSubStep(index, 'text', e.target.value)}
                            placeholder="Instruction text..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={subStep.photo_url || ''}
                              onChange={(e) => updateInstructionSubStep(index, 'photo_url', e.target.value)}
                              placeholder="Photo URL (Cloudinary)"
                              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <input
                              type="text"
                              value={subStep.safety_note || ''}
                              onChange={(e) => updateInstructionSubStep(index, 'safety_note', e.target.value)}
                              placeholder="Safety note (optional)"
                              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                          </div>
                        </div>
                        {editingStep.instruction_steps.length > 1 && (
                          <button
                            onClick={() => removeInstructionSubStep(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quality Checks */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Quality Checks (shown before completing)
                  </label>
                  <button
                    onClick={addQualityCheck}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add Check
                  </button>
                </div>
                <div className="space-y-2">
                  {editingStep.quality_checks.map((check, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={check}
                        onChange={(e) => updateQualityCheck(index, e.target.value)}
                        placeholder="e.g., Correct length verified"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={() => removeQualityCheck(index)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tool Requirements
                  </label>
                  <input
                    type="text"
                    value={editingStep.tool_requirements || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, tool_requirements: e.target.value })}
                    placeholder="e.g., Chop saw, tape measure"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estimated Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={editingStep.estimated_minutes || ''}
                    onChange={(e) =>
                      setEditingStep({ ...editingStep, estimated_minutes: parseInt(e.target.value) || undefined })
                    }
                    placeholder="e.g., 10"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  General Safety Note
                </label>
                <textarea
                  value={editingStep.safety_note || ''}
                  onChange={(e) => setEditingStep({ ...editingStep, safety_note: e.target.value })}
                  placeholder="Any safety warnings for this step..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingStep(null);
                  setIsNewStep(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStep}
                disabled={saving || !editingStep.step_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isNewStep ? 'Create Step' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
